/**
 * useModuleAccess — whether the current user holds at least `level` on a
 * module (e.g. `runner`) in the current tenant.
 *
 * Backed by GET /api/v1/tenants/{tenant_id}/modules/{module}/access, which
 * resolves the caller's highest effective module level in Keto
 * (admin > edit > operate > use > view).
 *
 * Fail-closed (SEC-003): loading/error/no-tenant resolve to `false`.
 */

import { useQuery } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';

export type ModuleLevel = 'admin' | 'edit' | 'operate' | 'use' | 'view';

const LEVEL_RANK: Record<ModuleLevel, number> = {
  admin: 5,
  edit: 4,
  operate: 3,
  use: 2,
  view: 1,
};

interface ModuleAccessResponse {
  module: string;
  tenant_id: string;
  level: ModuleLevel | null;
}

export function useModuleAccess(module: string, required: ModuleLevel): boolean {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id ?? '';

  const { data } = useQuery({
    queryKey: ['module-access', tenantId, module],
    queryFn: async () => {
      const { data } = await apiClient.get<ModuleAccessResponse>(
        `/api/v1/tenants/${tenantId}/modules/${module}/access`,
      );
      return data;
    },
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });

  if (!data?.level) return false;
  return LEVEL_RANK[data.level] >= LEVEL_RANK[required];
}